import { assocIn } from "icepick";
import { useCallback, useMemo } from "react";

import { PLUGIN_CUSTOM_VIZ } from "metabase/plugins/oss/custom-viz";
import {
  extractRemappings,
  getVisualizationTransformed,
} from "metabase/visualizations";
import { updateSettings } from "metabase/visualizations/lib/settings";
import { getSettingsWidgetsForSeries } from "metabase/visualizations/lib/widgets";
import type { ComputedVisualizationSettings } from "metabase/visualizations/types";
import type {
  SettingsExtra,
  Widget,
} from "metabase/visualizations/types/visualization";
import type Question from "metabase-lib/v1/Question";
import type {
  RawSeries,
  Series,
  TransformedSeries,
  VisualizationSettings,
} from "metabase-types/api";

export type UseChartSettingsStateProps = {
  settings?: VisualizationSettings;
  series: Series;
  onChange?: (
    settings: ComputedVisualizationSettings,
    question?: Question,
  ) => void;
};

export type UseChartSettingsStateReturned = {
  chartSettings?: VisualizationSettings;
  handleChangeSettings: (
    changedSettings: VisualizationSettings,
    question?: Question,
  ) => void;
  chartSettingsRawSeries: Series;
  transformedSeries?: RawSeries | TransformedSeries;
};

export const useChartSettingsState = ({
  settings,
  series,
  onChange,
}: UseChartSettingsStateProps): UseChartSettingsStateReturned => {
  const chartSettings = useMemo(
    () => settings || series[0].card.visualization_settings,
    [series, settings],
  );

  const handleChangeSettings = useCallback(
    (changedSettings: VisualizationSettings, question?: Question) => {
      onChange?.(updateSettings(chartSettings, changedSettings), question);
    },
    [chartSettings, onChange],
  );

  const chartSettingsRawSeries = useMemo(
    () => assocIn(series, [0, "card", "visualization_settings"], chartSettings),
    [chartSettings, series],
  );

  const transformedSeries = useMemo(() => {
    const { series: transformedSeries } = getVisualizationTransformed(
      extractRemappings(chartSettingsRawSeries),
    );
    return transformedSeries;
  }, [chartSettingsRawSeries]);

  return {
    chartSettings,
    handleChangeSettings,
    chartSettingsRawSeries,
    transformedSeries,
  };
};

export function useSettingsWidgets({
  series,
  transformedSeries,
  handleChangeSettings,
  isDashboard = false,
  extra,
}: {
  series: Series;
  transformedSeries?: RawSeries | TransformedSeries;
  handleChangeSettings: (settings: VisualizationSettings) => void;
  isDashboard?: boolean;
  extra?: SettingsExtra;
}): Widget[] {
  const display = series?.[0]?.card?.display;
  const { loading: customVizLoading } =
    PLUGIN_CUSTOM_VIZ.useAutoLoadCustomVizPlugin(display);

  const widgets = useMemo(
    () =>
      customVizLoading
        ? []
        : getSettingsWidgetsForSeries(
            transformedSeries,
            handleChangeSettings,
            isDashboard,
            extra,
          ),
    [
      customVizLoading,
      transformedSeries,
      handleChangeSettings,
      isDashboard,
      extra,
    ],
  );

  return widgets;
}
